import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import MoveCopyToProjectDialog from "~/components/MoveCopyToProject/MoveCopyToProjectDialog";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { MOVE_TASK_TO_PROJECT_SUCCESS } from "~/store/action_types";
import { moveCopyTaskToProject } from "~/store/actions/actions.tasks.jsx";
import { showErrorToast, showSuccessToast } from "../Toast";

const MoveCopyTaskToProjectContainer = ({ open, taskId, projectId, onCancel, onSubmit, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: sheetOpen, closeSheet } = useObjectSheetContext();

  const handleSubmit = useCallback(
    async (attributes) => {
      if (typeof onSubmit === "function") {
        onSubmit();
      }
      const { project_id, copy = false, redirect = true } = attributes;
      await dispatch(moveCopyTaskToProject(projectId, taskId, { project_id, copy }))
        .then((action) => {
          const { newProjectId = 0, response = {}, type } = action;
          const { result = {} } = response;

          if (typeof onSuccess === "function") {
            onSuccess();
          }
          if (type === MOVE_TASK_TO_PROJECT_SUCCESS) {
            showSuccessToast(copy ? t("Task copied to project") : t("Task moved to project"));
          } else {
            showErrorToast(copy ? t("Failed to copy task") : t("Failed to move task"));
          }
          const { single = 0 } = result;
          if (newProjectId > 0 && single > 0) {
            if (redirect) {
              navigate(`/projects/${newProjectId}/tasks/${single}`);
            } else if (sheetOpen) {
              closeSheet();
            } else {
              navigate(`/projects/${projectId}`);
            }
          }
        })
        .catch(() => null);
    },
    [onSubmit, dispatch, projectId, taskId, onSuccess, t, sheetOpen, navigate, closeSheet]
  );

  const excludeIds = useMemo(() => [projectId], [projectId]);

  return (
    <MoveCopyToProjectDialog
      open={open}
      explainer={t("Moving the task between projects will disconnect it from all of its dependencies.")}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      excludeIds={excludeIds}
      type="Task"
    />
  );
};

MoveCopyTaskToProjectContainer.propTypes = {
  open: PropTypes.bool,
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
};

MoveCopyTaskToProjectContainer.defaultProps = {
  open: false,
};

export default MoveCopyTaskToProjectContainer;
